import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';
import WebGLDotBackground from '../components/shared/WebGLDotBackground';

/* ─── ICONS ──────────────────────────────────────────────── */
const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const IconBuilding = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);
const IconShield = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

/* ─── TEAM LOGIN PAGE ────────────────────────────────────── */
const TeamLoginPage = () => {
  const [accessKey, setAccessKey] = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const navigate = useNavigate();
  // Synchronous guard against double-submit — see LoginPage.js for why
  // `disabled={loading}` alone isn't enough.
  const submittingRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/team-login`, { access_key: accessKey });
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('access_type', 'team_key');
      localStorage.setItem('team_profile_id', data.profile.id);
      localStorage.setItem('team_name', data.team_name || '');
      localStorage.setItem('user', JSON.stringify({
        id: data.profile.id,
        name: data.profile.name,
        email: 'team_access',
        is_admin: false,
      }));
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Clave de acceso inválida. Verifica e intenta nuevamente.');
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <div className="au-root">
      <WebGLDotBackground/>
      <div className="au-wrap">
        <div className="au-card">

          <div className="au-logo">
            <div className="au-logo-mark">
              <ContactLogo clickable/>
            </div>
          </div>

          <button type="button" className="au-back" onClick={() => navigate('/login')}>
            <IconArrowLeft/> Volver al inicio de sesión
          </button>

          <div className="au-team-badge">
            <IconBuilding/> Acceso corporativo
          </div>

          <div className="au-head" style={{marginTop:12}}>
            <h1 className="au-title">Contact for Teams</h1>
            <p className="au-sub">Ingresa la clave de acceso de tu equipo</p>
          </div>

          <form className="au-form" onSubmit={handleSubmit} noValidate>
            {error && <div className="au-error" role="alert">{error}</div>}

            <div className="au-field">
              <label htmlFor="au-key" className="au-label">Clave de acceso</label>
              <input
                id="au-key" type="text" autoComplete="off" required
                value={accessKey} onChange={e => setAccessKey(e.target.value)}
                className="au-input au-input--mono" placeholder="XXXX-XXXX-XXXX-XXXX"
                spellCheck={false}
              />
              <p className="au-hint">Solicita esta clave al administrador de tu equipo</p>
            </div>

            <button type="submit" disabled={loading} className="au-btn-primary" style={{marginTop:4}}>
              {loading ? <span className="au-spinner" aria-label="Verificando"/> : 'Entrar a mi perfil'}
            </button>
          </form>

          <div className="au-footer">
            <div className="au-trust">
              <IconShield/> Acceso seguro y encriptado
            </div>
          </div>
        </div>
      </div>
      <style>{CSS}</style>
    </div>
  );
};

/* ─── CSS ────────────────────────────────────────────────── */
const CSS = `
:root{--au-text:#ffffff;--au-muted:#9a9a9a;--au-faint:#666666;--au-border:#262626;--au-accent:#0071e3;--au-btn-bg:#ededed;--au-btn-text:#0a0a0a}
*{box-sizing:border-box;margin:0;padding:0}

.au-root{font-family:'Inter',system-ui,sans-serif;background:#000;min-height:100dvh;display:flex;flex-direction:column;color:var(--au-text);overflow-x:hidden;position:relative}

/* DOT-GRID WEBGL BACKGROUND */
.au-dotbg{position:fixed;inset:0;z-index:0;pointer-events:none}
.au-dotbg-canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
.au-dotbg-vignette{position:absolute;inset:0;background:radial-gradient(circle at center,rgba(0,0,0,.78) 0%,rgba(0,0,0,0) 100%)}

/* LOGO */

.au-wrap{position:relative;z-index:2;flex:1;display:flex;align-items:center;justify-content:center;padding:32px 20px}

.au-card{width:100%;max-width:420px;background:#121212;border:1px solid #222;border-radius:16px;padding:40px 36px;box-shadow:0 10px 50px rgba(0,0,0,.7);animation:auCardIn .7s cubic-bezier(.22,1,.36,1) both}
@keyframes auCardIn{from{opacity:0;transform:translateY(24px) scale(.98)}to{opacity:1;transform:translateY(0) scale(1)}}

.au-logo{display:flex;justify-content:center;margin-bottom:24px}
.au-head{margin-bottom:24px}
.au-title{font-size:22px;font-weight:700;line-height:1.2;letter-spacing:-.02em;color:var(--au-text);margin-bottom:5px}
.au-sub{font-size:14px;color:var(--au-muted)}

.au-error{background:rgba(220,38,38,.12);border:1px solid rgba(220,38,38,.35);color:#ff6b6b;padding:10px 14px;border-radius:8px;font-size:13px;line-height:1.5}

.au-form{display:flex;flex-direction:column;gap:16px}
.au-field{display:flex;flex-direction:column;gap:5px}
.au-label{font-size:13px;font-weight:600;color:var(--au-text)}
.au-label-row{display:flex;align-items:center;justify-content:space-between}

.au-input{width:100%;height:48px;padding:0 14px;border:1.5px solid var(--au-border);border-radius:10px;font-family:'Inter',system-ui,sans-serif;font-size:15px;color:var(--au-text);background:#000;outline:none;transition:border-color .15s,box-shadow .15s}
.au-input::placeholder{color:var(--au-faint)}
.au-input:focus{border-color:var(--au-accent);box-shadow:0 0 0 3px rgba(0,113,227,.18)}
.au-input--pw{padding-right:46px}
.au-input--mono{font-family:'Menlo','Monaco','Consolas',monospace;font-size:14px;letter-spacing:.06em}

.au-pw-wrap{position:relative}
.au-pw-toggle{position:absolute;right:0;top:0;width:46px;height:48px;display:flex;align-items:center;justify-content:center;background:none;border:none;color:var(--au-faint);cursor:pointer;transition:color .15s}
.au-pw-toggle:hover{color:var(--au-muted)}

.au-hint{font-size:12px;color:var(--au-muted);margin-top:2px;line-height:1.5}

.au-btn-primary{width:100%;height:48px;border-radius:100px;background:var(--au-btn-bg);color:var(--au-btn-text);border:none;font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1),box-shadow .2s}
.au-btn-primary:hover{opacity:.87;transform:translateY(-1px);box-shadow:0 6px 20px rgba(255,255,255,.1)}
.au-btn-primary:active{transform:scale(.97);opacity:1;box-shadow:none}
.au-btn-primary:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none}

.au-divider{position:relative;text-align:center;padding:2px 0}
.au-divider::before{content:'';position:absolute;top:50%;left:0;right:0;height:1px;background:var(--au-border)}
.au-divider span{position:relative;background:#121212;padding:0 12px;font-size:12px;color:var(--au-faint)}

.au-btn-google{width:100%;height:48px;border-radius:100px;background:transparent;border:1.5px solid var(--au-border);color:var(--au-text);font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:500;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:9px;transition:background .15s,border-color .15s}
.au-btn-google:hover{background:rgba(255,255,255,.06);border-color:#3a3a3a}
.au-btn-google:disabled{opacity:.4;cursor:not-allowed}

@keyframes auSpin{to{transform:rotate(360deg)}}
.au-spinner{width:16px;height:16px;border:2px solid rgba(0,0,0,.2);border-top-color:#0a0a0a;border-radius:50%;animation:auSpin .55s linear infinite;flex-shrink:0}

.au-footer{margin-top:24px;display:flex;flex-direction:column;align-items:center;gap:10px}
.au-footer-text{font-size:13px;color:var(--au-muted);text-align:center}
.au-link{font-weight:600;color:var(--au-text);background:none;border:none;cursor:pointer;font-family:inherit;font-size:inherit;transition:opacity .15s;padding:0}
.au-link:hover{opacity:.6}
.au-link-sm{font-size:12px;color:var(--au-muted);background:none;border:none;cursor:pointer;font-family:inherit;transition:color .15s;display:inline-flex;align-items:center;gap:5px;padding:0}
.au-link-sm:hover{color:var(--au-text)}

.au-back{display:inline-flex;align-items:center;gap:6px;font-size:13px;color:var(--au-muted);background:none;border:none;cursor:pointer;font-family:inherit;transition:color .15s;padding:0;margin-bottom:20px}
.au-back:hover{color:var(--au-text)}

.au-team-badge{display:inline-flex;align-items:center;gap:8px;border:1px solid var(--au-border);border-radius:100px;padding:6px 14px;font-size:12px;color:var(--au-muted);background:rgba(255,255,255,.04)}

.au-trust{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--au-faint)}

@media(max-width:480px){
  .au-wrap{padding:12px}
  .au-card{padding:24px 20px;border-radius:14px}
  .au-logo{margin-bottom:14px}
  .au-logo-mark img{height:26px}
  .au-back{margin-bottom:14px}
  .au-head{margin-bottom:14px}
  .au-title{font-size:19px}
  .au-sub{font-size:12.5px}
  .au-form{gap:11px}
  .au-field{gap:4px}
  .au-input{height:42px;font-size:14px}
  .au-btn-primary{height:42px;font-size:14px}
  .au-footer{margin-top:14px;gap:6px}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default TeamLoginPage;
